import {
	Button,
	Card,
	CheckboxField,
	DateControl,
	Empty,
	Icon,
	Id,
	Loading,
	TextField,
	WarningBanner,
	isSuccess,
} from "@dgs/core";
import { useField, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { hotelSectionService } from "../../../shared/api/hotelSectionService";
import {
	HotelResource,
	HotelSectionFormValues,
	HotelStayFormValues,
	HotelStayStatus,
	HotelsByDateRangeRequest,
	StayConfigurationResource,
} from "../../../shared/types/hotel";
import { useRegistrationForm } from "../../provider/registrationFormProviderContext";
import { CardList } from "./CardList";
import { HotelCard } from "./HotelCard";
import { HotelRoomCard } from "./HotelRoomCard";
import { HotelStayRemoveAction } from "./HotelStayRemoveAction";
import { HotelStayStatusLabel } from "./HotelStayStatusLabel";
import { HotelStayListItemHeading, HotelStayProcessedInfo } from "./HotestayList";
import { TitledSection } from "./TitledSection";

interface Props {
	name: string;
	registrationFormId: Id;
	guestId: Id | null;
	sectionId: number;
	heading: string;
	stayConfiguration: StayConfigurationResource;
	otherStays: HotelStayFormValues[];
	onChange: (hotelStay: HotelStayFormValues) => void;
	onDelete: () => void;
	isDeletable: boolean;
	multipleStaysAllowed: boolean;
}

const Container = styled(Card)<{ $hasError: boolean }>`
	display: flex;
	flex-direction: column;
	background: ${({ theme }) => theme.colors.palette.grey[10]};
	padding: ${({ theme }) => theme.spacing(4)};
	gap: ${({ theme }) => theme.spacing(4)};

	${({ theme, $hasError }) =>
		$hasError &&
		css`
			border-color: ${theme.colors.palette.danger.main.base};
		`}
`;

const CheckInOutDate = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.mobile} {
		grid-template-columns: 1fr 1fr;
	}
`;

const Actions = styled.div`
	display: flex;
	gap: ${({ theme }) => theme.spacing(2)};
	justify-content: flex-end;
`;

const HeadingWrapper = styled.div`
	display: flex;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: center;
`;

const InfoIcon = styled(Icon)`
	height: 1.2rem;
	width: 1.2rem;
`;

const Errors = styled.ul`
	list-style: none;
	grid-area: errors;
	background: ${({ theme }) => theme.colors.palette.danger.main.base};
	color: ${({ theme }) => theme.colors.palette.danger.main.contrast};
	padding: ${({ theme }) => theme.spacing(2)};
`;

const ListItem = styled.li`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(2)};
`;

const StyledCheckboxField = styled(CheckboxField)<{ $zeroMargin: boolean }>`
	${({ $zeroMargin: $zero }) =>
		$zero &&
		css`
			margin: 0;
		`};
`;

export const HotelStay: FC<Props> = ({
	name,
	otherStays,
	sectionId,
	guestId,
	heading,
	stayConfiguration,
	onChange,
	onDelete,
	isDeletable,
	multipleStaysAllowed,
}) => {
	const { t } = useTranslation();
	const titleId = useId();
	const errorId = useId();
	const { setFieldTouched } = useFormikContext<HotelSectionFormValues>();
	const { getHotelStayValidation } = useRegistrationForm();
	const [areHotelsLoading, setAreHotelsLoading] = useState<boolean>(false);
	const [hotels, setHotels] = useState<HotelResource[] | null>(null);
	const [showBanner, setShowBanner] = useState<boolean>(false);

	const [{ value: hotelStay }, { error, touched }] = useField<HotelStayFormValues>({
		name: name,
		validate: getHotelStayValidation(stayConfiguration, otherStays),
	});

	const [isEditEnabled, setIsEditEnabled] = useState(hotelStay.status === HotelStayStatus.INQUIRY);

	const loadHotels = useCallback(
		async (checkIn: string, checkOut: string) => {
			if (!stayConfiguration.isPreferredHotelSelectable) {
				return [];
			}
			setAreHotelsLoading(true);
			const request: HotelsByDateRangeRequest = {
				checkIn: checkIn,
				checkOut: checkOut,
				guestId: guestId,
			};
			const res = await hotelSectionService.getHotels(sectionId, request);

			if (isSuccess(res)) {
				setHotels(res.data.data);
				setAreHotelsLoading(false);
				return res.data.data;
			}
			setAreHotelsLoading(false);
			return [];
		},
		[guestId, sectionId, stayConfiguration.isPreferredHotelSelectable],
	);

	const applyHotelPreselection = useCallback(
		(newHotels: HotelResource[], updates: Partial<HotelStayFormValues> = {}) => {
			if (newHotels.length === 1) {
				if (newHotels[0].rooms.length === 1) {
					onChange({
						...hotelStay,
						...updates,
						requestedHotelId: newHotels[0].id,
						requestedRoomTypeId: newHotels[0].rooms[0].id,
					});
				} else {
					onChange({ ...hotelStay, ...updates, requestedHotelId: newHotels[0].id });
				}
			}
		},
		[hotelStay, onChange],
	);

	const handleChangeCheckInOutDate = useCallback(
		async (checkIn: string, checkOut: string) => {
			let newHotels: HotelResource[] = [];

			if (checkIn && checkOut && (checkIn !== hotelStay.checkIn || checkOut !== hotelStay.checkOut)) {
				newHotels = await loadHotels(checkIn, checkOut);
			}

			if (newHotels.length === 1) {
				applyHotelPreselection(newHotels, { checkIn, checkOut });
			} else {
				onChange({ ...hotelStay, checkIn, checkOut });
			}

			if (hotelStay.checkIn && hotelStay.checkOut) {
				void setFieldTouched(`${name}.checkIn`);
				void setFieldTouched(`${name}.checkOut`);
			}
		},
		[applyHotelPreselection, hotelStay, loadHotels, name, onChange, setFieldTouched],
	);

	useEffect(() => {
		if (hotelStay.checkIn && hotelStay.checkOut) {
			loadHotels(hotelStay.checkIn, hotelStay.checkOut).then(applyHotelPreselection);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const selectedHotel = useMemo(
		() => hotels?.find((hotel) => hotel.id === hotelStay.requestedHotelId),
		[hotelStay.requestedHotelId, hotels],
	);

	return (
		<ListItem
			aria-labelledby={titleId}
			aria-invalid={!!error && touched}
			aria-errormessage={!!error && touched ? errorId : undefined}
		>
			<HeadingWrapper>
				<HotelStayListItemHeading id={titleId}>{heading}</HotelStayListItemHeading>
				<HotelStayStatusLabel status={hotelStay.status} isCancellationRequested={hotelStay.cancellationRequested} />
			</HeadingWrapper>
			{hotelStay.status !== HotelStayStatus.INQUIRY && (
				<HotelStayProcessedInfo>
					<InfoIcon $icon="info" />
					{t("This inquiry has already been processed. You may only request changes or cancellation of this stay.")}
				</HotelStayProcessedInfo>
			)}
			<Container $hasError={!!error && touched} $variant="outlined">
				<CheckInOutDate>
					<DateControl
						label={`${t("Check-in")}*`}
						name={`${name}.checkIn`}
						min={hotelStay.mainStay ? hotelStay.mainStay.checkIn : stayConfiguration.earliestCombinedCheckIn}
						max={hotelStay.mainStay ? hotelStay.mainStay.checkOut : stayConfiguration.latestCombinedCheckOut}
						value={hotelStay.checkIn}
						disabled={!isEditEnabled}
						onChange={async (e) => {
							await handleChangeCheckInOutDate(e.target.value || "", hotelStay.checkOut);
						}}
					/>
					<DateControl
						label={`${t("Check-out")}*`}
						name={`${name}.checkOut`}
						min={hotelStay.mainStay ? hotelStay.mainStay.checkIn : stayConfiguration.earliestCombinedCheckIn}
						max={hotelStay.mainStay ? hotelStay.mainStay.checkOut : stayConfiguration.latestCombinedCheckOut}
						value={hotelStay.checkOut}
						disabled={!isEditEnabled}
						onChange={async (e) => {
							await handleChangeCheckInOutDate(hotelStay.checkIn, e.target.value || "");
						}}
					/>
				</CheckInOutDate>
				{areHotelsLoading && <Loading />}
				{stayConfiguration.isPreferredHotelSelectable && hotels && (
					<TitledSection title={t("Select hotel")}>
						{hotels.length === 0 && <Empty>{t("No hotels found for the given check-in and check-out date.")}</Empty>}
						<CardList>
							{hotels.map((hotel) => (
								<li key={hotel.id}>
									<HotelCard
										hotel={hotel}
										isSelected={hotel === selectedHotel}
										disabled={!isEditEnabled}
										onSelect={() => {
											setShowBanner(false);
											onChange({
												...hotelStay,
												requestedHotelId: hotel.id,
												requestedRoomTypeId: null,
											});
										}}
									/>
								</li>
							))}
						</CardList>
					</TitledSection>
				)}
				{stayConfiguration.enableQuotaWarning && showBanner && (
					<WarningBanner
						warning={t(
							"The quota has already been reached for at least one of the nights. A request is still possible, but availability cannot be guaranteed.",
						)}
					/>
				)}
				{stayConfiguration.isPreferredRoomTypeSelectable && selectedHotel && (
					<TitledSection title={t("Select room")}>
						<CardList>
							{selectedHotel.rooms.map((room) => (
								<li key={room.id}>
									<HotelRoomCard
										room={room}
										isSelected={room.id === hotelStay.requestedRoomTypeId}
										disabled={!isEditEnabled}
										showRoomCapacityInFrontend={stayConfiguration.showRoomCapacityInFrontend}
										addedAdditionalPersons={hotelStay?.additionalPersonsEnabled ?? false}
										onSelect={() => {
											setShowBanner(!room.canBeRequested);
											onChange({
												...hotelStay,
												requestedHotelId: selectedHotel.id,
												requestedRoomTypeId: room.id,
											});
										}}
									/>
								</li>
							))}
						</CardList>
					</TitledSection>
				)}
				{stayConfiguration.allowAdditionalPersons && (
					<StyledCheckboxField
						$zeroMargin={hotelStay.additionalPersonsEnabled}
						label={t("I want to add another person to my stay")}
						name={`${name}.additionalPersonsEnabled`}
						onChange={(e) => {
							if (!e.target.value) {
								onChange({
									...hotelStay,
									additionalPersonsText: null,
									additionalPersonsEnabled: e.target.value,
								});
							}
						}}
					/>
				)}
				{hotelStay.additionalPersonsEnabled && (
					<TextField
						label={t("Additional person")}
						name={`${name}.additionalPersonsText`}
						description={t("Please make sure that the person named is aware of your information.")}
					/>
				)}
				{stayConfiguration.allowRemarkByGuest && (
					<TextField
						disabled={!isEditEnabled}
						label={t("Remarks for stay")}
						name={`${name}.remarkByGuest`}
						description={t(
							"This information is voluntary and is used to provide information for the hotel. Please do not enter any health related information.",
						)}
						placeholder={t("e.g. late arrival at XX o'clock / parking slot required etc.")}
					/>
				)}
				<Actions>
					{hotelStay.status !== HotelStayStatus.INQUIRY && !hotelStay.cancellationRequested && !isEditEnabled && (
						<Button prefix={<Icon $icon="edit" />} type="button" onClick={() => setIsEditEnabled(true)}>
							{t("Request changes")}
						</Button>
					)}
					{multipleStaysAllowed && (
						<HotelStayRemoveAction
							wayOfBooking={stayConfiguration.wayOfBooking}
							hotelStay={hotelStay}
							onDelete={onDelete}
							onChange={(value) => {
								onChange(value);
								setIsEditEnabled(false);
							}}
							disabled={!isDeletable}
						/>
					)}
				</Actions>
			</Container>
			{error && touched && (
				<Errors id={errorId}>
					<li>{error}</li>
				</Errors>
			)}
		</ListItem>
	);
};
